import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Upload, Trash2, Settings, Plus, User as UserIcon, Lock } from 'lucide-react';
import profileService, { ProfileData } from '../services/profileService';
import axios from 'axios';
import api from '../services/api';
import './ProfilePage.scss';

interface CVTemplate {
  id: number;
  category: string;
  value: string;
  label: string;
}

interface CompanyUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  company_role: 'owner' | 'user';
  status: string;
  profile_image: string | null;
  created_at: string;
}

const ProfilePage: React.FC = () => {
  const { user, isAuthenticated, isLoading, refreshProfile } = useAuth();
  const [activeTab, setActiveTab] = useState<'company' | 'users'>('company');
  const [companyActivityOptions, setCompanyActivityOptions] = useState<CVTemplate[]>([]);
  const [formData, setFormData] = useState<ProfileData>({
    company_name: '',
    company_code: '',
    vat_code: '',
    company_address: '',
    company_activity: '',
    contact_person: '',
    phone: '',
    website_url: '',
    location: '',
    bio: '',
    company_size: '1-10',
    linkedin_url: '',
    is_recruitment_agency: false
  });

  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Delete image confirm modal
  const [showDeleteImageConfirm, setShowDeleteImageConfirm] = useState(false);

  // Change password modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Delete account modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Company users (Vartotojai tab)
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [companyUsersLoading, setCompanyUsersLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
  const [userFormData, setUserFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', company_role: 'user' as 'owner' | 'user'
  });
  const [userFormError, setUserFormError] = useState<string | null>(null);
  const [userFormLoading, setUserFormLoading] = useState(false);
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState<number | null>(null);

  // Load company activity options from CV templates
  useEffect(() => {
    const loadCompanyActivityOptions = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/cv-templates?grouped=true`);
        if (response.data?.data?.company_activity) {
          setCompanyActivityOptions(response.data.data.company_activity);
        }
      } catch (error) {
        console.error('Error loading company activity options:', error);
      }
    };
    loadCompanyActivityOptions();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        company_name: user.company_name || '',
        company_code: user.company_code || '',
        vat_code: (user as any).vat_code || '',
        company_address: (user as any).company_address || '',
        company_activity: user.company_activity || '',
        contact_person: user.contact_person || '',
        phone: user.phone || '',
        website_url: user.profile?.website_url || '',
        location: user.profile?.location || '',
        bio: user.profile?.bio || '',
        company_size: user.profile?.company_size || '1-10',
        linkedin_url: user.profile?.linkedin_url || '',
        is_recruitment_agency: !!user.is_recruitment_agency
      });

      // Set current profile image
      if (user.profile?.profile_image) {
        const imageUrl = profileService.getProfileImageUrl(user.profile.profile_image);
        if (imageUrl) {
          setPreviewUrl(imageUrl);
        }
      }
    }
  }, [user]);

  const fetchCompanyUsers = useCallback(async () => {
    setCompanyUsersLoading(true);
    try {
      const response = await api.get('/company-users');
      if (response.data?.success) setCompanyUsers(response.data.data);
    } catch (err) {
      console.error('Error loading company users:', err);
    } finally {
      setCompanyUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'users') fetchCompanyUsers();
  }, [activeTab, fetchCompanyUsers]);

  const openCreateUserModal = () => {
    setEditingUser(null);
    setUserFormData({ first_name: '', last_name: '', email: '', phone: '', password: '', company_role: 'user' });
    setUserFormError(null);
    setShowUserModal(true);
  };

  const openEditUserModal = (u: CompanyUser) => {
    setEditingUser(u);
    setUserFormData({
      first_name: u.first_name || '', last_name: u.last_name || '',
      email: u.email || '', phone: u.phone || '', password: '', company_role: u.company_role
    });
    setUserFormError(null);
    setShowUserModal(true);
  };

  const handleUserFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserFormError(null);
    setUserFormLoading(true);
    try {
      if (editingUser) {
        const payload: any = { ...userFormData };
        if (!payload.password) delete payload.password;
        await api.put(`/company-users/${editingUser.id}`, payload);
      } else {
        if (!userFormData.password) { setUserFormError('Slaptažodis privalomas'); setUserFormLoading(false); return; }
        await api.post('/company-users', userFormData);
      }
      setShowUserModal(false);
      fetchCompanyUsers();
    } catch (err: any) {
      setUserFormError(err.response?.data?.message || 'Klaida išsaugant vartotoją');
    } finally {
      setUserFormLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      await api.delete(`/company-users/${userId}`);
      setShowDeleteUserConfirm(null);
      fetchCompanyUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
    }
  };

  // Show loading state while auth is being checked
  if (isLoading) {
    return (
      <div className="profile-page">
        <div className="loading">Kraunama...</div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  // Redirect employees to their specific profile page
  if (user?.user_type === 'employee') {
    return <Navigate to="/profile/employee" replace />;
  }

  // Only allow employers and admins to access this page
  if (user?.user_type !== 'employer' && user?.user_type !== 'admin') {
    return <Navigate to="/" replace />;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Nauji slaptažodžiai nesutampa');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Naujas slaptažodis turi būti bent 6 simbolių');
      return;
    }
    try {
      setPasswordLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_URL}/profile/change-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword: passwordForm.currentPassword, newPassword: passwordForm.newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setShowPasswordModal(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setSuccess('Slaptažodis sėkmingai pakeistas');
      } else {
        setPasswordError(data.message || 'Nepavyko pakeisti slaptažodžio');
      }
    } catch {
      setPasswordError('Serverio klaida');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setDeleteError(null);
    try {
      setDeleteLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res = await fetch(`${API_URL}/profile/account`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: deletePassword })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.removeItem('auth_token');
        window.location.href = '/';
      } else {
        setDeleteError(data.message || 'Nepavyko pašalinti paskyros');
      }
    } catch {
      setDeleteError('Serverio klaida');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = () => {
    setShowDeleteImageConfirm(true);
  };

  const confirmDeleteImage = async () => {
    setShowDeleteImageConfirm(false);
    try {
      setLoading(true);
      const result = await profileService.deleteProfileImage();
      if (result.success) {
        setPreviewUrl(null);
        setProfileImage(null);
        setSuccess('Profilio nuotrauka sėkmingai ištrinta');
        await refreshProfile();
      } else {
        setError(result.message || 'Nepavyko ištrinti profilio nuotraukos');
      }
    } catch (err: any) {
      setError('Įvyko klaida trinant profilio nuotrauką');
    } finally {
      setLoading(false);
    }
  };

  const normalizeUrl = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const dataToSave = {
      ...formData,
      website_url: normalizeUrl(formData.website_url || ''),
    };

    try {
      const result = await profileService.updateProfileWithImage(dataToSave, profileImage || undefined);
      if (result.success) {
        setSuccess('Profilis sėkmingai atnaujintas!');
        setProfileImage(null);
        refreshProfile();
      } else {
        setError(result.message || 'Nepavyko atnaujinti profilio');
      }
    } catch (err: any) {
      setError('Įvyko klaida atnaujinant profilį');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="profile-page">
      <div className="container">
        {/* Navigation */}
        <nav className="profile-nav">
          <span className="breadcrumb">Titulinis &gt; Darbdavio profilis</span>
        </nav>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'company' ? 'active' : ''}`}
            onClick={() => setActiveTab('company')}
          >
            Įmonės duomenys
          </button>
          <button 
            className={`tab-btn ${activeTab === 'users' ? 'active' : ''} ${!formData.is_recruitment_agency ? 'locked' : ''}`}
            onClick={() => formData.is_recruitment_agency && setActiveTab('users')}
            title={!formData.is_recruitment_agency ? 'Prieinama tik Personalo atrankų įmonėms' : undefined}
            disabled={!formData.is_recruitment_agency}
          >
            Vartotojai
            {!formData.is_recruitment_agency && <Lock size={13} />}
          </button>
        </div>


        {/* Content */}
        <div className="profile-content">
          {activeTab === 'company' && (
            <div className="company-section">
              <div className="profile-layout">
                {/* Left Sidebar */}
                <div className="profile-sidebar">
                  <div className="logo-section">
                    <div className="logo-upload">
                      {previewUrl ? (
                        <div className="logo-preview">
                          <img src={previewUrl} alt="Company Logo" />
                          <button 
                            type="button" 
                            className="delete-logo-btn"
                            onClick={handleDeleteImage}
                            disabled={loading}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ) : (
                        <div className="logo-placeholder">
                          <div className="logo-icon">📷</div>
                        </div>
                      )}
                    </div>
                    
                    <div className="logo-actions">
                      <label className="upload-btn">
                        <Upload size={16} />
                        ĮKELTI LOGOTIPĄ
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                    
                    <p className="upload-note">
                      Nuotrauka turi būti JPG, GIF, PNG formatu. Maksimalus dydis: 5 MB
                    </p>
                  </div>
                </div>

                {/* Main Form */}
                <div className="profile-form">
                  <div className="profile-top-actions">
                    <button type="button" className="profile-action-btn secondary" onClick={() => { setPasswordError(null); setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); setShowPasswordModal(true); }}>
                      🔒 Pakeisti slaptažodį
                    </button>
                    <button type="button" className="profile-action-btn danger" onClick={() => { setDeleteError(null); setDeletePassword(''); setShowDeleteModal(true); }}>
                      🗑️ Pašalinti paskyrą
                    </button>
                  </div>

                  <form onSubmit={handleSubmit}>
                    {error && <div className="error-message">{error}</div>}
                    {success && <div className="success-message">{success}</div>}

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="company_name">Įmonės pavadinimas *</label>
                        <input
                          type="text"
                          id="company_name"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group checkbox-group">
                        <label>&nbsp;</label>
                        <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '8px 0' }}>
                          <input
                            type="checkbox"
                            checked={!!formData.is_recruitment_agency}
                            onChange={(e) => setFormData(prev => ({ ...prev, is_recruitment_agency: e.target.checked }))}
                            style={{ width: 16, height: 16, accentColor: '#23928B', cursor: 'pointer' }}
                          />
                          <span>Personalo atrankų įmonė</span>
                        </label>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="company_code">Įmonės kodas *</label>
                        <input
                          type="text"
                          id="company_code"
                          name="company_code"
                          value={formData.company_code}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="company_activity">Įmonės veiklos sritis *</label>
                        <select
                          id="company_activity"
                          name="company_activity"
                          value={formData.company_activity}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Pasirinkite sritį</option>
                          {companyActivityOptions.map((option) => (
                            <option key={option.id} value={option.value}>
                              {option.value}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="contact_person">Kontaktinis asmuo *</label>
                        <input
                          type="text"
                          id="contact_person"
                          name="contact_person"
                          value={formData.contact_person}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone">Telefonas</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="user_email">El. paštas *</label>
                        <input
                          type="email"
                          id="user_email"
                          value={user?.email || ''}
                          disabled
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="website_url">Įmonės internetinė svetainė</label>
                        <input
                          type="text"
                          id="website_url"
                          name="website_url"
                          value={formData.website_url}
                          onChange={handleInputChange}
                          placeholder="pvz. https://mano-imone.lt"
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group full-width">
                        <label htmlFor="bio">Trumpas įmonės veiklos aprašymas</label>
                        <textarea
                          id="bio"
                          name="bio"
                          value={formData.bio}
                          onChange={handleInputChange}
                          rows={3}
                          placeholder="Trumpai aprašykite savo įmonės veiklą..."
                        />
                      </div>
                    </div>

                    <div className="form-actions">
                      <button 
                        type="button" 
                        className="btn-cancel-custom"
                        onClick={() => window.history.back()}
                      >
                        ✖ ATSAUKTI
                      </button>
                      <button 
                        type="submit" 
                        className="btn-save-custom"
                        disabled={loading}
                      >
                        ✓ IŠSAUGOTI PAKEITIMUS
                      </button>
                    </div>

                  </form>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="users-section">
              <div className="users-toolbar">
                <div className="users-toolbar-left">
                  <label className="select-all-label">
                    <span>Žymėti visus</span>
                  </label>
                </div>
                <button className="btn-add-user" onClick={openCreateUserModal}>
                  <Plus size={16} /> Naujas vartotojas
                </button>
              </div>

              {companyUsersLoading ? (
                <div className="users-loading">Kraunama...</div>
              ) : (
                <div className="users-grid">
                  {companyUsers.map(cu => (
                    <div key={cu.id} className={`user-card ${cu.company_role === 'owner' ? 'owner' : ''}`}>
                      <div className="user-card-actions">
                        <button className="icon-btn" onClick={() => openEditUserModal(cu)} title="Redaguoti">
                          <Settings size={16} />
                        </button>
                        {cu.company_role !== 'owner' && (
                          <button className="icon-btn danger" onClick={() => setShowDeleteUserConfirm(cu.id)} title="Ištrinti">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>

                      <div className="user-card-avatar">
                        {cu.profile_image ? (
                          <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/uploads/profiles/${cu.profile_image}`} alt="" />
                        ) : (
                          <div className="avatar-placeholder">
                            <UserIcon size={40} />
                          </div>
                        )}
                      </div>

                      <div className="user-card-role">
                        {cu.company_role === 'owner' ? 'Administratorius' : 'Vartotojas'}
                      </div>
                      <div className="user-card-name">
                        {cu.first_name} {cu.last_name}
                      </div>

                      <div className="user-card-contacts">
                        {cu.phone && <span className="contact-item">📞 {cu.phone}</span>}
                        {cu.email && <span className="contact-item">✉ {cu.email}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Delete Image Confirm Modal */}
    {showDeleteImageConfirm && (
      <div className="profile-modal-overlay" onClick={() => setShowDeleteImageConfirm(false)}>
        <div className="profile-modal" onClick={e => e.stopPropagation()}>
          <div className="profile-modal-header">
            <h3>Ištrinti logotipą</h3>
            <button className="profile-modal-close" onClick={() => setShowDeleteImageConfirm(false)}>×</button>
          </div>
          <div className="profile-modal-body">
            <p style={{ margin: 0, color: '#444', fontSize: '15px' }}>
              Ar tikrai norite ištrinti profilio nuotrauką / logotipą?
            </p>
            <div className="profile-modal-footer">
              <button className="btn-cancel" onClick={() => setShowDeleteImageConfirm(false)}>Atšaukti</button>
              <button className="btn-danger" onClick={confirmDeleteImage}>Ištrinti</button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Change Password Modal */}
    {showPasswordModal && (
      <div className="profile-modal-overlay" onClick={() => setShowPasswordModal(false)}>
        <div className="profile-modal" onClick={e => e.stopPropagation()}>
          <div className="profile-modal-header">
            <h3>Pakeisti slaptažodį</h3>
            <button className="profile-modal-close" onClick={() => setShowPasswordModal(false)}>×</button>
          </div>
          <form onSubmit={handleChangePassword} className="profile-modal-body">
            {passwordError && <div className="profile-modal-error">{passwordError}</div>}
            <div className="profile-modal-field">
              <label>Dabartinis slaptažodis</label>
              <input
                type="password"
                value={passwordForm.currentPassword}
                onChange={e => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))}
                required
                autoComplete="current-password"
              />
            </div>
            <div className="profile-modal-field">
              <label>Naujas slaptažodis</label>
              <input
                type="password"
                value={passwordForm.newPassword}
                onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="profile-modal-field">
              <label>Pakartokite naują slaptažodį</label>
              <input
                type="password"
                value={passwordForm.confirmPassword}
                onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="profile-modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowPasswordModal(false)}>Atšaukti</button>
              <button type="submit" className="btn-confirm" disabled={passwordLoading}>
                {passwordLoading ? 'Keičiama...' : 'Pakeisti'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Delete Account Modal */}
    {showDeleteModal && (
      <div className="profile-modal-overlay" onClick={() => setShowDeleteModal(false)}>
        <div className="profile-modal" onClick={e => e.stopPropagation()}>
          <div className="profile-modal-header">
            <h3>Pašalinti paskyrą</h3>
            <button className="profile-modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
          </div>
          <form onSubmit={handleDeleteAccount} className="profile-modal-body">
            <p className="profile-modal-warning">
              ⚠️ Šis veiksmas negrįžtamas. Visi jūsų duomenys bus ištrinti.
            </p>
            {deleteError && <div className="profile-modal-error">{deleteError}</div>}
            <div className="profile-modal-field">
              <label>Patvirtinkite slaptažodžiu</label>
              <input
                type="password"
                value={deletePassword}
                onChange={e => setDeletePassword(e.target.value)}
                required
                placeholder="Įveskite slaptažodį"
              />
            </div>
            <div className="profile-modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Atšaukti</button>
              <button type="submit" className="btn-danger" disabled={deleteLoading}>
                {deleteLoading ? 'Šalinama...' : 'Pašalinti paskyrą'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Create / Edit Company User Modal */}
    {showUserModal && (
      <div className="profile-modal-overlay" onClick={() => setShowUserModal(false)}>
        <div className="profile-modal" onClick={e => e.stopPropagation()}>
          <div className="profile-modal-header">
            <h3>{editingUser ? 'Redaguoti vartotoją' : 'Naujas vartotojas'}</h3>
            <button className="profile-modal-close" onClick={() => setShowUserModal(false)}>×</button>
          </div>
          <form onSubmit={handleUserFormSubmit} className="profile-modal-body">
            {userFormError && <div className="profile-modal-error">{userFormError}</div>}
            <div className="profile-modal-row">
              <div className="profile-modal-field">
                <label>Vardas *</label>
                <input
                  type="text"
                  value={userFormData.first_name}
                  onChange={e => setUserFormData(p => ({ ...p, first_name: e.target.value }))}
                  required
                />
              </div>
              <div className="profile-modal-field">
                <label>Pavardė *</label>
                <input
                  type="text"
                  value={userFormData.last_name}
                  onChange={e => setUserFormData(p => ({ ...p, last_name: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="profile-modal-field">
              <label>El. paštas *</label>
              <input
                type="email"
                value={userFormData.email}
                onChange={e => setUserFormData(p => ({ ...p, email: e.target.value }))}
                required
                disabled={!!editingUser && editingUser.company_role === 'owner'}
              />
            </div>
            <div className="profile-modal-field">
              <label>Telefonas</label>
              <input
                type="tel"
                value={userFormData.phone}
                onChange={e => setUserFormData(p => ({ ...p, phone: e.target.value }))}
              />
            </div>
            <div className="profile-modal-field">
              <label>{editingUser ? 'Naujas slaptažodis (palikite tuščią jei nekeičiate)' : 'Slaptažodis *'}</label>
              <input
                type="password"
                value={userFormData.password}
                onChange={e => setUserFormData(p => ({ ...p, password: e.target.value }))}
                required={!editingUser}
                autoComplete="new-password"
              />
            </div>
            <div className="profile-modal-field">
              <label>Rolė</label>
              <select
                value={userFormData.company_role}
                onChange={e => setUserFormData(p => ({ ...p, company_role: e.target.value as 'owner' | 'user' }))}
              >
                <option value="user">Vartotojas</option>
                <option value="owner">Administratorius</option>
              </select>
            </div>
            <div className="profile-modal-footer">
              <button type="button" className="btn-cancel" onClick={() => setShowUserModal(false)}>Atšaukti</button>
              <button type="submit" className="btn-confirm" disabled={userFormLoading}>
                {userFormLoading ? 'Saugoma...' : (editingUser ? 'Išsaugoti' : 'Sukurti')}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Delete User Confirm Modal */}
    {showDeleteUserConfirm !== null && (
      <div className="profile-modal-overlay" onClick={() => setShowDeleteUserConfirm(null)}>
        <div className="profile-modal" onClick={e => e.stopPropagation()}>
          <div className="profile-modal-header">
            <h3>Ištrinti vartotoją</h3>
            <button className="profile-modal-close" onClick={() => setShowDeleteUserConfirm(null)}>×</button>
          </div>
          <div className="profile-modal-body">
            <p className="profile-modal-warning">
              Ar tikrai norite ištrinti šį vartotoją? Šis veiksmas negrįžtamas.
            </p>
            <div className="profile-modal-footer">
              <button className="btn-cancel" onClick={() => setShowDeleteUserConfirm(null)}>Atšaukti</button>
              <button className="btn-danger" onClick={() => handleDeleteUser(showDeleteUserConfirm)}>Ištrinti</button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ProfilePage;
